import * as React from "react";
import { Outlet } from "react-router-dom";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/layout/theme-provider";
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { AssistantRail, useAssistantRail } from "@/components/assistant";

interface AppLayoutProps {
  /** Optional explicit content. Falls back to the router <Outlet />. */
  children?: React.ReactNode;
}

/**
 * Authenticated application shell: fixed sidebar rail, sticky header with global
 * search + theme toggle + user menu, and a scrollable content region. Mounts the
 * theme provider, tooltip provider, and the Sonner toast surface once.
 *
 * Frontend Integration renders this inside the `<Authenticated>` boundary in
 * App.tsx, with resource/custom routes nested under it.
 */
export function AppLayout({ children }: AppLayoutProps) {
  // The Assistant rail is mounted once at the shell so its conversation survives
  // route changes (frontend-ux-design.md §4.3). State (open + scope) is shared
  // with the rail and persists to localStorage; the Header's Sparkles button +
  // ⌘J toggle it via the `aos:toggle-assistant` event.
  const assistant = useAssistantRail();

  return (
    <ThemeProvider defaultTheme="system">
      <TooltipProvider delayDuration={200}>
        <div className="flex min-h-screen bg-background text-foreground">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <Header />
            <main className="flex-1 overflow-y-auto px-6 py-4 md:px-8">
              {children ?? <Outlet />}
            </main>
          </div>
          <AssistantRail state={assistant} />
        </div>
        <Toaster position="bottom-right" richColors closeButton />
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default AppLayout;
